/**
 * Tabs density / geometry — the shape axis.
 * Color lives in Tabs.color.ts; composition in Tabs.styles.ts.
 */

import type { TabsOrientation, TabsSize, TabsVariant } from './Tabs.types';

/** Label / gap typography per size. */
export const TABS_TYPE: Record<TabsSize, string> = {
  sm: 'text-xs font-body gap-1.5',
  md: 'text-sm font-body gap-2',
  lg: 'text-base font-body gap-2.5',
};

/** Horizontal line trigger box. */
export const TABS_LINE_TRIGGER: Record<TabsSize, string> = {
  sm: 'h-9 px-3',
  md: 'h-11 px-3.5',
  lg: 'h-12 px-4',
};

/** Horizontal default (pill) trigger box. */
export const TABS_PILL_TRIGGER: Record<TabsSize, string> = {
  sm: 'h-7 px-2.5',
  md: 'h-8 px-3.5',
  lg: 'h-9 px-4',
};

/** Vertical trigger padding. */
export const TABS_VERTICAL_TRIGGER: Record<TabsSize, string> = {
  sm: 'px-2.5 py-2 gap-2',
  md: 'px-3 py-2.5 gap-2.5',
  lg: 'px-3.5 py-3 gap-3',
};

/** Track padding for horizontal default. */
export const TABS_PILL_TRACK: Record<TabsSize, string> = {
  sm: 'p-0.5 gap-0.5',
  md: 'p-1 gap-0.5',
  lg: 'p-1 gap-1',
};

/** Leading icon glyph size — owned by Tabs `size`, not call-site classes when possible. */
export const TABS_ICON_GLYPH: Record<TabsSize, string> = {
  sm: '[&_svg]:h-3.5 [&_svg]:w-3.5',
  md: '[&_svg]:h-4 [&_svg]:w-4',
  lg: '[&_svg]:h-5 [&_svg]:w-5',
};

/** Count pill geometry. */
export const TABS_COUNT: Record<TabsSize, string> = {
  sm: 'min-w-[18px] h-[18px] px-1 text-[10px]',
  md: 'min-w-[20px] h-5 px-1.5 text-[11px]',
  lg: 'min-w-[22px] h-6 px-2 text-xs',
};

export function getTabsTriggerSizeClass(
  orientation: TabsOrientation,
  variant: TabsVariant,
  size: TabsSize,
): string {
  if (orientation === 'vertical') {
    return TABS_VERTICAL_TRIGGER[size];
  }
  if (variant === 'line') {
    return TABS_LINE_TRIGGER[size];
  }
  return TABS_PILL_TRIGGER[size];
}
